import React, { useState, useEffect } from 'react';
import './App.css';
import Landing from './components/Landing';
import Onboarding from './components/Onboarding';
import Today from './components/Today';
import Journal from './components/Journal';
import Goals from './components/Goals';
import Library from './components/Library';
import Foundations from './components/Foundations';
import Profile from './components/Profile';
import NavBar from './components/NavBar';

const OUTSETA_DOMAIN = 'be-here-now.outseta.com';

function App() {
  const [screen, setScreen] = useState('loading');
  const [user, setUser] = useState(null);


  // Hard fallback - never stay on loading screen more than 3 seconds
  // But only if there's no token being processed
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasToken = params.get('access_token');
    const hasStoredToken = localStorage.getItem('outseta_token');
    // If no token at all, show landing fast
    // If token exists, give more time to authenticate
    const delay = hasToken || hasStoredToken ? 8000 : 3000;
    const fallback = setTimeout(() => {
      setScreen(prev => prev === 'loading' ? 'landing' : prev);
    }, delay);
    return () => clearTimeout(fallback);
  }, []);

  useEffect(() => {
    // Check for token in URL params (from Outseta redirect)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('access_token');
    const storedToken = localStorage.getItem('outseta_token');

    if (urlToken) {
      console.log('Token found in URL — setting on Outseta...');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Wait for Outseta to load then set token
      waitForOutsetaThenSetToken(urlToken, 0);
    } else if (storedToken) {
      // Have a stored token — try to authenticate
      setTimeout(() => waitForOutseta(0), 500);
    } else {
      // No token at all — show landing immediately
      setTimeout(() => setScreen('landing'), 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function waitForOutsetaThenSetToken(token, attempts) {
    if (typeof window.Outseta === 'undefined') {
      if (attempts < 20) {
        setTimeout(() => waitForOutsetaThenSetToken(token, attempts + 1), 500);
      } else {
        setScreen('landing');
      }
      return;
    }
    console.log('Setting token on Outseta...');
    try {
      // Set the token directly on Outseta
      if (window.Outseta.setAccessToken) {
        window.Outseta.setAccessToken(token);
      }
      if (window.Outseta.profile && window.Outseta.profile.setAccessToken) {
        window.Outseta.profile.setAccessToken(token);
      }
      // Also store in localStorage as backup
      localStorage.setItem('outseta_token', token);
    } catch(e) {
      console.log('Error setting token:', e.message);
    }
    setTimeout(() => checkAuthAsync(0), 1000);
  }

  function waitForOutseta(attempts) {
    if (typeof window.Outseta === 'undefined') {
      if (attempts < 6) {
        setTimeout(() => waitForOutseta(attempts + 1), 300);
      } else {
        setScreen('landing');
      }
      return;
    }

    // Listen for auth events — but only register ONCE ever.
    // Without this guard, every retry of waitForOutseta would register
    // another listener, and every accessToken.set event would trigger
    // multiple checkAuthAsync calls, cascading into an infinite loop
    // that crashes the browser with ERR_INSUFFICIENT_RESOURCES.
    if (!window.__bhnAuthListenerRegistered) {
      window.__bhnAuthListenerRegistered = true;
      try {
        window.Outseta.on('accessToken.set', () => {
          console.log('accessToken.set event fired');
          // Only trigger a check if we aren't already in one.
          if (!window.__bhnAuthCheckInFlight) {
            setTimeout(() => checkAuthAsync(0), 500);
          }
        });
      } catch(e) {}
    }

    checkAuthAsync(0);
  }

  async function checkAuthAsync(retries) {
    // Guard: only one auth check may run at a time. Prevents overlapping
    // requests that could still cascade into a runaway loop.
    if (window.__bhnAuthCheckInFlight) {
      console.log('Auth check already in flight — skipping');
      return;
    }
    window.__bhnAuthCheckInFlight = true;

    try {
      console.log('Checking auth, attempt', retries);

      // Check token multiple ways
      let token = null;
      try { token = window.Outseta.getAccessToken(); } catch(e) {}
      if (!token) {
        try { token = window.Outseta?.auth?.getAccessToken(); } catch(e) {}
      }
      if (!token) token = localStorage.getItem('outseta_token');
      if (!token) {
        // Try getting from Outseta profile
        try { token = window.Outseta?.profile?.getAccessToken?.(); } catch(e) {}
      }

      console.log('Token present:', token ? 'yes' : 'no');

      if (!token && retries < 2) {
        window.__bhnAuthCheckInFlight = false;
        setTimeout(() => checkAuthAsync(retries + 1), 1000);
        return;
      }

      if (!token) {
        window.__bhnAuthCheckInFlight = false;
        setScreen('landing');
        return;
      }

      // NOTE: previously called window.Outseta.setAccessToken(token) here,
      // but doing so fired an accessToken.set event that re-entered this
      // function, causing an infinite loop and browser crash. The token
      // was already set upstream (in waitForOutsetaThenSetToken) — no need
      // to set it again on every auth check.

      // Get user via Promise
      const member = await window.Outseta.getUser();
      console.log('Member email:', member ? member.Email : 'null');

      if (!member) {
        if (retries < 5) {
          window.__bhnAuthCheckInFlight = false;
          setTimeout(() => checkAuthAsync(retries + 1), 2000);
        } else {
          localStorage.removeItem('outseta_token');
          setScreen('landing');
        }
        return;
      }

      const subscription = member?.Account?.CurrentSubscription;
      const accountStageNum = member?.Account?.AccountStage;
      const accountStageLabel = member?.Account?.AccountStageLabel;
      console.log('AccountStage:', accountStageNum, '| Label:', accountStageLabel, '| HasSubscription:', !!subscription);

      // Grant access if has any subscription OR accountStage >= 2 (trialing or active)
      const hasAccess = (!!subscription) || (accountStageNum >= 2);
      console.log('hasAccess:', hasAccess);

      if (hasAccess) {
        console.log('Access granted!');
        setUser(member);
        const userId = member?.Uid || member?.Email;
        // Check Supabase for onboarded status first
        try {
          const { loadUserData } = await import('./supabase');
          const data = await loadUserData(userId);
          const onboardedSupabase = data?.onboarded;
          const onboardedLocal = localStorage.getItem('bhn_onboarded');
          if (!onboardedSupabase && !onboardedLocal) {
            setScreen('onboarding');
          } else {
            // Sync localStorage with Supabase
            if (onboardedSupabase) localStorage.setItem('bhn_onboarded', 'true');
            const lastScreen = localStorage.getItem('bhn_last_screen');
            const validScreens = ['today', 'journal', 'goals', 'foundations', 'library', 'profile'];
            setScreen(validScreens.includes(lastScreen) ? lastScreen : 'today');
          }
        } catch(e) {
          // Fallback to localStorage
          const onboarded = localStorage.getItem('bhn_onboarded');
          if (!onboarded) {
            setScreen('onboarding');
          } else {
            const lastScreen = localStorage.getItem('bhn_last_screen');
            const validScreens = ['today', 'journal', 'goals', 'foundations', 'library', 'profile'];
            setScreen(validScreens.includes(lastScreen) ? lastScreen : 'today');
          }
        }
      } else {
        console.log('No access, accountStage:', accountStageNum);
        setScreen('landing');
      }
    } catch (e) {
      console.log('Auth error:', e.message);
      if (retries < 3) {
        window.__bhnAuthCheckInFlight = false;
        setTimeout(() => checkAuthAsync(retries + 1), 2000);
      } else {
        setScreen('landing');
      }
    } finally {
      // Always release the in-flight flag on successful completion so a
      // future explicit auth check (e.g. after user action) can run.
      // Scheduled retries reset the flag themselves before returning above.
      window.__bhnAuthCheckInFlight = false;
    }
  }

  function handleLogin() {
    // Direct link to Outseta login — token comes back in URL
    window.location.href = `https://${OUTSETA_DOMAIN}/auth?widgetMode=login&postLoginUrl=https://app.beherenow.biz`;
  }

  function handleSignup(plan) {
    const MONTHLY_PLAN = 'wmj5xPWV';
    const ANNUAL_PLAN = 'jW7weVWq';
    const planUid = plan === 'annual' ? ANNUAL_PLAN : MONTHLY_PLAN;
    window.location.href = `https://be-here-now.outseta.com/auth?widgetMode=register&planUid=${planUid}&postRegistrationUrl=https://app.beherenow.biz?signup=1`;
  }

  function handleLogout() {
    localStorage.removeItem('bhn_onboarded');
    localStorage.removeItem('outseta_token');
    try {
      if (window.Outseta?.auth?.logout) {
        window.Outseta.auth.logout();
      }
    } catch(e) {}
    setUser(null);
    setScreen('landing');
  }

  async function completeOnboarding() {
    localStorage.setItem('bhn_onboarded', 'true');
    // Also save to Supabase so any device knows they've onboarded
    try {
      const { saveField } = await import('./supabase');
      const userId = user?.Uid || user?.Email;
      if (userId) await saveField(userId, 'onboarded', true);
    } catch(e) { console.log('Supabase onboarded save error:', e); }
    setScreen('today');
  }

  

  

  const mainScreens = ['today', 'journal', 'goals', 'foundations', 'library', 'profile'];

  function navigateTo(newScreen) {
    if (mainScreens.includes(newScreen)) {
      localStorage.setItem('bhn_last_screen', newScreen);
    }
    setScreen(newScreen);
  }
  const showNav = mainScreens.includes(screen);

  // Check if just signed up - show confirm email screen
  const params_check = new URLSearchParams(window.location.search);
  if (params_check.get('signup') === '1' && (screen === 'landing' || screen === 'loading')) {
    return (
      <div className="app-container" style={{ background: "#FFFFFF" }}>
        <div className="phone-frame" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '40px 24px' }}>
          <div style={{ textAlign: 'center', maxWidth: 340 }}>
            <div style={{ fontSize: 48, marginBottom: 20 }}>📧</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#111', letterSpacing: '-0.5px', marginBottom: 12, lineHeight: 1.2 }}>Check your email</div>
            <div style={{ fontSize: 15, color: '#555', lineHeight: 1.8, marginBottom: 32 }}>
              We sent a confirmation email to your inbox. Click the link to set your password and activate your account.
            </div>
            <div style={{ background: '#F8FCF6', border: '1px solid #E8F0E4', borderRadius: 12, padding: '16px 20px', marginBottom: 32, textAlign: 'left' }}>
              <div style={{ fontSize: 13, color: '#3A6A2A', fontWeight: 700, marginBottom: 8 }}>Next steps:</div>
              <div style={{ fontSize: 13, color: '#555', lineHeight: 1.9 }}>
                1. Open the confirmation email<br/>
                2. Click the link to set your password<br/>
                3. You'll be taken straight into the app
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#A09888', lineHeight: 1.7, marginBottom: 24 }}>
              Didn't get it? Check your spam folder or contact us at info@beherenow.biz
            </div>
            <button className="btn-secondary" style={{ width: '100%' }} onClick={() => {
              window.history.replaceState({}, document.title, window.location.pathname);
              setScreen('landing');
            }}>Back to sign in</button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'loading') {
    return (
      <div className="app-container" style={{ background: "#FFFFFF" }}>
        <div className="phone-frame" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div style={{ textAlign: 'center' }}>
            <img src="/logo-transparent.png" alt="Be Here Now" style={{ width: 110, height: 'auto', marginBottom: 20 }} onError={e => { e.target.style.display='none'; }} />
            <div style={{ fontSize: 10, letterSpacing: '0.2em', color: '#A09888', textTransform: 'uppercase', marginBottom: 8, fontWeight: 500 }}>Be Here Now</div>
            <div style={{ fontSize: 12, color: '#C8C4BE', letterSpacing: '0.04em' }}>Loading your practice...</div>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="app-container" style={{ background: "#FFFFFF" }}>
      <div className="phone-frame">
        {screen === 'landing' && (
          <Landing onSignup={handleSignup} onLogin={handleLogin} />
        )}
        {screen === 'onboarding' && (
          <Onboarding onComplete={completeOnboarding} />
        )}
        {screen === 'today' && (
          <Today onNavigate={navigateTo} userId={user?.Uid || user?.Email} />
        )}
        {screen === 'journal' && <Journal userId={user?.Uid || user?.Email} />}
        {screen === 'goals' && <Goals userId={user?.Uid || user?.Email} />}
        {screen === 'library' && <Library userId={user?.Uid || user?.Email} />}
        {screen === 'foundations' && <Foundations userId={user?.Uid || user?.Email} />}

        {screen === 'profile' && (
          <Profile user={user} onLogout={handleLogout} />
        )}
        {showNav && (
          <NavBar current={screen} onNavigate={navigateTo} />
        )}
      </div>
    </div>
  );
}

export default App;
